// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module defines the core types and schemas for location-related data in the Hardfin system.
 * It includes definitions for locations, time series data, and various location attributes.
 */
import { sparseLocationSchema } from '@/sparse/api/types';
import { integrationListSchema } from '@/utilities/integration';
import { z } from 'zod';

/**
 * Defines valid location types in the system.
 */
export const locationTypeSchema = z.enum(['SITE', 'ZONE']);

export type LocationType = z.infer<typeof locationTypeSchema>;

/**
 * Complete location schema that extends sparse location with additional asset information.
 * Used for detailed views where full location information is needed.
 */
export const locationSchema = sparseLocationSchema.extend({
  assetCount: z.number(), // Total number of assets at this location
  upcomingProjectCount: z.number(), // Number of upcoming projects
  activeProjectCount: z.number(), // Number of active projects
  pastProjectCount: z.number(), // Number of past projects
  incompleteProjectCount: z.number(), // Number of incomplete date range projects
  integrations: integrationListSchema, // Integration links
  parentLocation: z.lazy(() => sparseLocationSchema).nullable(), // Parent site reference (zones only)
});

export type Location = z.infer<typeof locationSchema>;
